/**
 * `TableRoot` — the scroll/virtualization root `useDataTable` measures (attach its
 * `scrollRef` here; `useVirtualizer` reads it via `getScrollElement`). It is the
 * stacking + overflow context the sticky header and pinned columns live in.
 *
 * A thin element: `role`/`aria-*`/`data-*`/`style` all pass through, so the grid
 * semantics (`role="grid"`, `aria-rowcount`, `aria-busy`) layer on with no
 * signature change.
 */
import type { HTMLAttributes, Ref } from 'react'

import { cn } from '@gto/ui/cn'

import { type DataTableDensity, dataTableRootVariants } from './data-table.styles'

export type TableRootProps = HTMLAttributes<HTMLDivElement> & {
  ref?: Ref<HTMLDivElement>
  /** Echoed onto `data-density` so descendants + tooling can read the mode. */
  density?: DataTableDensity
  /** Clip horizontal overflow instead of scrolling it. See `dataTableRootVariants`. */
  clipHorizontalOverflow?: boolean
  /**
   * Hide the scrollbars entirely (scrolling still works) — the compact dialog-table
   * look the custom-solution config tables opt into. Default SHOWS the DS's styled 6px
   * bars: #5362 applied the hide unconditionally here and every DataTable silently lost
   * its scrollbars (`scrollbar-width: none` also defeats the `::-webkit-scrollbar`
   * styling in Chromium) — DataGridPro parity wants them visible.
   */
  hideScrollbars?: boolean
}

const TableRoot = ({
  className,
  density,
  clipHorizontalOverflow,
  hideScrollbars = false,
  ref,
  ...props
}: TableRootProps) => {
  return (
    <div
      ref={ref}
      data-density={density}
      className={cn(
        dataTableRootVariants({ clipHorizontalOverflow }),
        hideScrollbars &&
          '[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        className,
      )}
      {...props}
    />
  )
}

export { TableRoot }
